import * as CNPJ from 'cnpj';
import * as CPF from 'cpf';
import { brazilCountry } from '../lib/countries';
import { Address, Seller } from '../modules/customer/types/customer.types';
import { Locales } from '../modules/translation/messages';
import { GetAddressDTO } from '../types/address.types';
import { FormatCappedCountParams, FormatCityWithStateParams } from './formatters.types';

const BILLION = 1000000000;
const MILLION = 1000000;
const THOUSAND = 1000;

export const empty2Null = (value?: string | null): string | null => {
  if (!value || value.trim().length === 0) {
    return null;
  }
  return value;
};

export const pluralize = (quantity: number, singular: string, plural: string) =>
  quantity && quantity > 1 ? plural : singular;

export const toTextValue = (value?: string | null) => value ?? '';

export const formatNumberPtBr = (number: string | number) => number.toLocaleString('pt-BR');

const abbreviateThousands = (value: number) =>
  value > THOUSAND - 1 ? `${Math.floor(value / THOUSAND)}k` : value;

export const formatCappedCount = ({
  count,
  max,
  isAbbreviated = false,
  locale = Locales.PORTUGUESE_BR,
}: FormatCappedCountParams) => {
  if (count > max) {
    return `${isAbbreviated ? abbreviateThousands(max) : max.toLocaleString(locale)}+`;
  }

  return isAbbreviated ? abbreviateThousands(count) : count;
};

export const formatCNPJ = (cnpj?: string) => {
  if (cnpj) {
    try {
      return CNPJ.format(cnpj);
    } catch {
      return '';
    }
  }
  return '';
};

export const formatCPF = (cpf?: string) => {
  if (cpf) {
    try {
      return CPF.format(cpf);
    } catch {
      return '';
    }
  }
  return '';
};

export const formatCpfCnpjForList = (cpfcnpj: string | undefined, isPJ: boolean) => {
  if (!cpfcnpj) {
    return '-';
  }

  if (isPJ) {
    return formatCNPJ(cpfcnpj);
  }

  return formatCPF(cpfcnpj);
};

export const formatCityWithState = ({ city, state }: FormatCityWithStateParams) => {
  if (!city) {
    return '-';
  }

  return state ? `${city} - ${state}` : city;
};

export const phoneUnmask = (value: any) => {
  if (value === undefined || value === null) {
    return '';
  }
  return value.replace(/\D/g, '');
};

const fallbackIfUndefined = (defaultFallback: string, value?: string) =>
  typeof value === 'undefined' ? defaultFallback : value;

export const formatBrazilPhoneNumber = (phoneNumber: string) => {
  const digits = phoneNumber.replace(/\D/g, '');

  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }

  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }

  return phoneNumber;
};

export const formatBrazilCellphone = (value?: string | null, emptyFallback?: string) => {
  if (!value) {
    return fallbackIfUndefined('Não cadastrado', emptyFallback);
  }
  const newValue = phoneUnmask(value);
  if (newValue === '') {
    return fallbackIfUndefined('Não cadastrado', emptyFallback);
  }
  return formatBrazilPhoneNumber(newValue);
};

export const formatGlobalCellphone = (value: string, countryCode: string) => {
  if (countryCode === brazilCountry.phone) {
    return `+${countryCode} ${formatBrazilPhoneNumber(value)}`;
  }

  return countryCode ? `+${countryCode} ${value}` : value;
};

export const formatCompleteName = (name?: string, surname?: string, defaultName: string = '') =>
  (name && surname && `${name} ${surname}`) || name || surname || defaultName;

export const formatOrderSummarySellerName = (seller?: Seller) =>
  formatCompleteName(seller?.name, seller?.surname);

export const joinNonEmpty = (parts: Array<string | undefined | null>, separator: string) =>
  parts.filter(Boolean).join(separator);

export const joinStrings = (list: Array<string>, locale: string) =>
  new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(list);

export const formatCompleteAddress = (address: Address | GetAddressDTO) => {
  const { streetDescription, streetNumber, complement, neighborhood, city, state, zipCode } =
    address;

  const complementString = complement ? ` ${complement}` : '';
  const streetNumberString = streetNumber ? `nº ${streetNumber}${complementString},` : '';
  const neighborhoodString = neighborhood ? `${neighborhood},` : '';

  return `${streetDescription}, ${streetNumberString} ${neighborhoodString} ${city} - ${state}, ${zipCode}`;
};

export const percentToHex = (percentage: number) => {
  const intValue = Math.round((percentage / 100) * 255);
  const hexValue = intValue.toString(16);
  return hexValue.padStart(2, '0').toUpperCase();
};

export const formatLocaleAbreviatedNumber = (
  value: number,
  locale: Locales,
  isMoney = false,
): string => {
  if (value > BILLION) {
    return `${Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value / BILLION)}B`;
  }

  if (value > MILLION) {
    return `${Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value / MILLION)}M`;
  }

  if (value > THOUSAND) {
    return `${Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value / THOUSAND)}K`;
  }

  return Intl.NumberFormat(locale, { maximumFractionDigits: isMoney ? 2 : 1 }).format(value);
};

export const formatPercentNumberString = (percent: number | string, locale?: Locales) =>
  locale ? percent?.toLocaleString(locale).concat('%') : percent?.toString().concat('%');

export const getFormattedCodeName = (name?: string, code?: string): string => {
  if (!name) {
    return '';
  }

  return code ? `${code} - ${name}` : name;
};
